// src/pages/register/RegisterDoctor.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth } from "../../firebase";
import { stripeIdentityStatus } from "../../api";
import Turnstile from "../../components/Turnstile";
import {
  Stethoscope,
  ShieldCheck,
  CheckCircle2,
  CalendarCheck,
  Inbox,
} from "lucide-react";
import Spinner from "../../components/Spinner";
import FilePickerField from "../../components/FilePickerField";
import StripeIdentityVerifyPanel from "../../components/StripeIdentityVerifyPanel";
import { PendingApprovalCard } from "../../components/PendingApprovalCard";
import {
  COUNTRY_OPTIONS,
  getCountryRule,
  getCurrencyForCountry,
  getDefaultRegionForCountry,
  isValidPhoneForCountry,
  isValidPostalCode,
  normalizeCountryCode,
  phonePlaceholderForCountry,
  normalizeBusinessTaxId,
  isValidBusinessTaxId,
  getBusinessTaxIdFieldLabel,
  getBusinessTaxIdShortLabel,
  getBusinessTaxIdPlaceholder,
  businessTaxIdValidationMessage,
  getProfessionalRegistrationFieldRule,
  toE164Phone,
} from "../../countryRules";
const HIDDEN_TURNSTILE_STYLE: React.CSSProperties = {
  position: "absolute",
  left: -9999,
  top: "auto",
  width: 1,
  height: 1,
  overflow: "hidden",
};
const DOCTOR_API_BASE =
  import.meta.env.VITE_DOCTOR_API_BASE ||
  import.meta.env.VITE_API_DOCTOR_BASE ||
  import.meta.env.VITE_API_BASE;
const EUCA_MD_CONTACT = {
  practice: 'EUCA MD',
  email: 'contact@eucamd.com',
  phone: '0431 866 603',
  street: 'Level 1/457-459 Elizabeth St, Surry Hills',
  state: 'NSW',
  postcode: '2010',
};
const normalizeAbn = (value: string = '', country: unknown = 'AU') => normalizeBusinessTaxId(value, country);
const isValidAbn = (value: string = '', country: unknown = 'AU') => isValidBusinessTaxId(value, country)
const hasStripeIdentityVerified = (record: any) => {
  if (!record || typeof record !== "object") return false;
  if (record.stripeIdentityVerifiedAt) return true;
  const status = String(record.stripeIdentityStatus || "").trim().toLowerCase();
  return status === "verified" || status === "passed" || status === "success";
};
const normalizeSpec = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/s$/, '');
// ---- Specialties (unchanged list, but we no longer limit) ----
const SPECIALTY_GROUPS = {
  "Adult Medicine": [
    "General Practitioner",
    "Aesthetic Medicine",
    "Adult Cardiology",
    "Adult Clinical Genetics",
    "Adult Clinical Pharmacology",
    "Adult Dermatology",
    "Adult Endocrinology",
    "Adult Gastroenterology",
    "Adult General Medicine",
    "Internal Medicine",
    "Adult Geriatric Medicine",
    "Adult Haematology",
    "Adult Hepatology",
    "Adult Immunology & Allergy",
    "Adult Infectious Diseases",
    "Adult Medical Oncology",
    "Adult Nephrology",
    "Adult Neurology",
    "Adult Nuclear Medicine",
    "Adult Palliative Medicine",
    "Adult Pain Medicine",
    "Adult Rehabilitation Medicine",
    "Adult Respiratory & Sleep Medicine",
    "Adult Rheumatology",
    "Adult Sports & Exercise Medicine",
  ],
  "Medical Leadership & Administration": ["Hospital Executive"],
  "Adult Surgery & Procedural": [
    "Adult Cardiothoracic Surgery",
    "Adult Colorectal Surgery",
    "Adult General Surgery",
    "Adult Neurosurgery",
    "Adult Ophthalmology",
    "Adult Oral & Maxillofacial Surgery",
    "Adult Orthopaedic Surgery",
    "Adult Otolaryngology (ENT)",
    "Adult Plastic & Reconstructive Surgery",
    "Adult Urology",
    "Adult Vascular Surgery",
  ],
  "Women’s & Reproductive Health": [
    "Obstetrics & Gynaecology",
    "Reproductive Endocrinology & Fertility",
    "Maternal-Fetal Medicine",
    "Gynaecological Oncology",
    "Urogynaecology",
  ],
  "Mental Health": [
    "Adult Psychiatry",
    "Addiction Psychiatry",
    "Consultation-Liaison Psychiatry",
    "Forensic Psychiatry",
    "Old Age Psychiatry",
  ],
  "Paediatric Medicine": [
    "Paediatric Cardiology",
    "Paediatric Clinical Genetics",
    "Paediatric Clinical Pharmacology",
    "Paediatric Dermatology",
    "Paediatric Endocrinology",
    "Paediatric Gastroenterology",
    "Paediatric General Medicine",
    "Paediatric Haematology",
    "Paediatric Hepatology",
    "Paediatric Immunology & Allergy",
    "Paediatric Infectious Diseases",
    "Paediatric Medical Oncology",
    "Paediatric Metabolic Medicine",
    "Paediatric Nephrology",
    "Paediatric Neurology",
    "Paediatric Nuclear Medicine",
    "Paediatric Palliative Medicine",
    "Paediatric Pain Medicine",
    "Paediatric Rehabilitation Medicine",
    "Paediatric Respiratory & Sleep Medicine",
    "Paediatric Rheumatology",
    "Paediatric Sports & Exercise Medicine",
  ],
  "Paediatric Surgery & Procedural": [
    "Paediatric Cardiothoracic Surgery",
    "Paediatric General Surgery",
    "Paediatric Neurosurgery",
    "Paediatric Ophthalmology",
    "Paediatric Oral & Maxillofacial Surgery",
    "Paediatric Orthopaedic Surgery",
    "Paediatric Otolaryngology (ENT)",
    "Paediatric Plastic & Reconstructive Surgery",
    "Paediatric Urology",
    "Paediatric Vascular Surgery",
  ],
  "Child & Adolescent Mental Health": ["Paediatric Psychiatry"],
  Other: ["Doctor", "Founder"],
} as const;
export const ALL_SPECIALTIES: string[] = Object.values(SPECIALTY_GROUPS).flat();
// ---- utils ----
const slug = (t: string) => `sp-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
async function getTokenOrThrow() {
  const u = auth.currentUser;
  if (!u) throw new Error("Please sign in first.");
  return await u.getIdToken();
}
async function presign(keyHint: string, contentType: string) {
  const token = await getTokenOrThrow();
  const r = await fetch(DOCTOR_API_BASE + "/uploads/presign", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    body: JSON.stringify({ keyHint, contentType }),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.error || "Presign failed");
  return j as { url: string; fields: Record<string, string>; key?: string };
}
async function uploadToS3(p: { url: string; fields: Record<string, string> }, file: File) {
  const fd = new FormData();
  Object.entries(p.fields).forEach(([k, v]) => fd.append(k, v));
  fd.append("file", file);
  const up = await fetch(p.url, { method: "POST", body: fd });
  if (!up.ok) {
    let detail = '';
    try {
      detail = await up.text();
    } catch {
      detail = up.statusText;
    }
    throw new Error(detail || "Upload failed");
  }
}
const fileKeyFromPresign = (psg: any) => psg?.key || psg?.fields?.key || null;
// ---- shared section ----
const Section: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="section">
    {title && <h1 className="section-title">{title}</h1>}
    {children}
  </section>
);
type RegisterDoctorProps = {
  user?: any
  variant?: 'full' | 'inline'
  inlineNotice?: React.ReactNode
  onSubmitSuccess?: () => void
  initialProfile?: any
  adminMode?: boolean
  adminPresign?: (keyHint: string, contentType: string) => Promise<{ url: string; fields: Record<string, string>; key?: string }>
  onAdminSubmit?: (payload: any) => Promise<any>
}
export default function RegisterDoctors({
  user,
  variant = 'full',
  inlineNotice,
  onSubmitSuccess,
  initialProfile,
  adminMode = false,
  adminPresign,
  onAdminSubmit,
}: RegisterDoctorProps) {
  const isInline = variant === 'inline'
  const isAdmin = !!adminMode
  const SITE_KEY = !isAdmin ? (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) : undefined
  const isAuthed = isAdmin ? true : !!user;
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 🆕 server registration status
  // UNKNOWN = not loaded yet
  // NONE    = no registration yet
  // PENDING = submitted, awaiting approval
  // APPROVED= approved
  const [regStatus, setRegStatus] = useState<"UNKNOWN" | "NONE" | "PENDING" | "APPROVED">("UNKNOWN");
  const [regProfileType, setRegProfileType] = useState<"EUCA_MD" | "SOLO" | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(() => (!!user && !isAdmin));
  const prefillRef = useRef(false);
  // 👇 define this once
const DOCTOR_REG_ENDPOINTS = ["/work/doctors/me"] as const;

  // 👇 then your “load my registration” effect
  useEffect(() => {
    if (isAdmin) {
      setRegStatus("NONE");
      setRegProfileType(null);
      setLoadingStatus(false);
      return;
    }
    if (!user) {
      setRegStatus("NONE");
      setRegProfileType(null);
      setLoadingStatus(false); // 👈 important
      return;
    }
    (async () => {
      try {
        setLoadingStatus(true);
        const token = await getTokenOrThrow();
        let found = false;
        for (const ep of DOCTOR_REG_ENDPOINTS) {
          if (found) break;
          try {
            const r = await fetch(DOCTOR_API_BASE + ep, {
              headers: { Authorization: "Bearer " + token },
            });
            if (r.status === 404) continue;
            if (r.ok) {
              const text = await r.text();
              if (!text) continue; // empty body → not found here, try next endpoint
              let data: any;
              try {
                data = JSON.parse(text);
              } catch {
                continue; // not JSON we recognise, try next endpoint
              }
              // If this endpoint returns a list/array, skip it (not a "my profile" object)
              if (Array.isArray(data)) continue;

              // Prefer draft, then profile, else raw
              const payload = data.draftProfile || data.profile || data;
              setStripeIdentityVerified(
                (prev) =>
                  prev ||
                  hasStripeIdentityVerified(data) ||
                  hasStripeIdentityVerified(payload)
              );
              prefillFromProfile(payload, { force: true });

              const st = (data.status || "").toString().toUpperCase();
              if (st === "APPROVED") {
                setRegStatus("APPROVED");
                setRegProfileType((payload.profileType as any) ?? (data.profileType as any) ?? null);
                found = true;
                break;
              }
              if (st === "PENDING") {
                setRegStatus("PENDING");
                setRegProfileType((payload.profileType as any) ?? (data.profileType as any) ?? null);
                found = true;
                break;
              }
              // Unknown/none → keep looking
              continue;
            }
          } catch {
            // try next endpoint
          }
        }
        if (!found) {
          setRegStatus("NONE");
          setRegProfileType(null);
        }
      } catch {
        setRegStatus("NONE");
        setRegProfileType(null);
      } finally {
        setLoadingStatus(false);
      }
    })();
  }, [user, isAdmin]);
  // ---- single form state ----
  const displayNameParts = (user?.displayName || "").trim().split(/\s+/)
  const initialFirst = displayNameParts.length > 1 ? displayNameParts.slice(0, -1).join(" ") : displayNameParts[0] || ""
  const initialLast = displayNameParts.length > 1 ? displayNameParts.slice(-1).join(" ") : ""
const PROFILE_CHAR_LIMIT = 1000
  const loginEmailFromUser = (user?: any) => (user?.email || auth.currentUser?.email || '').trim()
  const loginEmail = useMemo(() => {
    if (isAdmin) {
      const src =
        initialProfile?.email ||
        initialProfile?.profile?.email ||
        initialProfile?.draftProfile?.email ||
        initialProfile?.adminContact?.email
      return (src || '').trim()
    }
    return loginEmailFromUser(user)
  }, [initialProfile, isAdmin, user])
const [s, setS] = useState<any>({
    firstName: initialFirst,
    lastName: initialLast,
    email: loginEmail,
    countryCode: "AU",
    phone: "",
    street: "",
    city: "",
    abn: "",
    state: "",
    postcode: "",
    ahpraNumber: "",
    languagesOther: "",
    specialties: [],
    customSpecialties: [],
    profile: "",
    cvFile: null,
    ahpraFile: null,
    photoFile: null,
    idFile1: null,
    practiceName: "",
    publicPractice: "",
    publicEmail: loginEmail,
    publicPhone: "",
    publicStreet: "",
    publicCity: "",
    publicState: "",
    publicPostcode: "",
    pricingMin: "",
    pricingMax: "",
    pricingMedicare: "",
    pricingNotes: "",
  });
  useEffect(() => {
    if (!loginEmail) return;
    setS((prev: any) => ({
      ...prev,
      email: loginEmail,
      publicEmail: prev.publicEmail || loginEmail,
    }));
  }, [loginEmail]);
  // specialties modal
const [specOpen, setSpecOpen] = useState(false);
const [specQuery, setSpecQuery] = useState("");
const [customSpec, setCustomSpec] = useState("");
const [customSpecErr, setCustomSpecErr] = useState("");
// Turnstile
const [tSpec, setTSpec] = useState("");
const [showSpecTS, setShowSpecTS] = useState(false);
const [waitingSpecTS, setWaitingSpecTS] = useState(false);
const [pricingErrors, setPricingErrors] = useState<Record<string, string>>({});
const [syncPublicContact, setSyncPublicContact] = useState(() => !initialProfile);
const prevPracticeRef = useRef<string | null>(null);
const prevPublicPhoneRef = useRef<string | null>(null);
const prevSyncPublicRef = useRef<boolean | null>(null);
const [showOptional, setShowOptional] = useState(false);
const [docKeys, setDocKeys] = useState<{ photoKey: string | null; ahpraKey: string | null; id1Key: string | null; cvKey: string | null }>({
  photoKey: null,
  ahpraKey: null,
  id1Key: null,
  cvKey: null,
});
const [stripeIdentityVerified, setStripeIdentityVerified] = useState(false);
  const countryCode = normalizeCountryCode(s.countryCode);
  const countryRule = getCountryRule(countryCode);
  const regionOptions = countryRule.regionOptions || [];
  const phonePlaceholder = phonePlaceholderForCountry(countryCode);
  const taxIdLabel = getBusinessTaxIdFieldLabel(countryCode)
  const taxIdShortLabel = getBusinessTaxIdShortLabel(countryCode)
  const taxIdPlaceholder = getBusinessTaxIdPlaceholder(countryCode)
  const taxIdError = businessTaxIdValidationMessage(countryCode)
  const doctorRegistrationRule = getProfessionalRegistrationFieldRule('doctor', countryCode)
  const feeCurrency = getCurrencyForCountry(countryCode);
  const fileNameFromKey = (k?: string | null) => {
    if (!k) return '';
    const parts = k.split('/');
    return parts[parts.length - 1] || k;
  };
  useEffect(() => {
    // retain placeholder refs for future use; no special affiliation toggles now
    prevPracticeRef.current = prevPracticeRef.current
    prevPublicPhoneRef.current = prevPublicPhoneRef.current
    prevSyncPublicRef.current = prevSyncPublicRef.current
  }, [syncPublicContact]);
  const steps = ["Identity & Compliance", "Specialties", "Pricing", "Documents & Verification"] as const;
  const [step, setStep] = useState(0);
  const [info, setInfo] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [validatedStep, setValidatedStep] = useState<number | null>(null);
  const [docsAttempted, setDocsAttempted] = useState(false);
  const handleDocSelect = useCallback(
    async (field: keyof typeof docKeys, file: File | null, hint: string) => {
      try {
        const presignFn = isAdmin && adminPresign ? adminPresign : presign;
        const setFile = (prev: any, f: File | null) => {
          if (field === "cvKey") return { ...prev, cvFile: f };
          if (field === "ahpraKey") return { ...prev, ahpraFile: f };
          if (field === "id1Key") return { ...prev, idFile1: f };
          if (field === "photoKey") return { ...prev, photoFile: f };
          return prev;
        };
        if (!file) {
          setDocKeys((prev) => ({ ...prev, [field]: null }));
          setS((prev: any) => setFile(prev, null));
          return;
        }
        setS((prev: any) => setFile(prev, file));
        const psg = await presignFn(hint, file.type || "application/octet-stream");
        await uploadToS3(psg, file);
        const key = fileKeyFromPresign(psg);
        setDocKeys((prev) => ({ ...prev, [field]: key }));
        setErr("");
      } catch (e: any) {
        setErr(e?.message || "File upload failed");
      }
    },
    [adminPresign, isAdmin]
  );
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveVisible, setSaveVisible] = useState(false);
  const saveTimerRef = useRef<number | null>(null);
  useEffect(() => {
    setErr("");
    setInfo("");
    setShowErrors(false);
    setValidatedStep(null);
    setDocsAttempted(false);
    setShowSpecTS(false);
    setWaitingSpecTS(false);
    setSubmitting(false);
    setTSpec("");
  }, [step]);

  useEffect(() => {
    if (step === 3) {
      // entering Documents step: clear any previous validation so we don't show upload errors by default
      setErr("");
      setShowErrors(false);
      setValidatedStep(null);
    }
  }, [step]);
  const buildDraftPayload = useCallback(() => {
    const contactEmail = (loginEmail || s.email || '').trim() || null;
    const publicEmail = syncPublicContact ? contactEmail : (s.publicEmail || '').trim() || null;
    const publicPhone = syncPublicContact ? s.phone : s.publicPhone;
    const publicStreet = syncPublicContact ? s.street : s.publicStreet;
    const publicCity = syncPublicContact ? s.city : s.publicCity;
    const publicState = syncPublicContact ? s.state : s.publicState;
    const publicPostcode = syncPublicContact ? s.postcode : s.publicPostcode;
    const pricingMin = Number(s.pricingMin);
    const pricingMax = Number(s.pricingMax);
    const pricing =
      Number.isFinite(pricingMin) || Number.isFinite(pricingMax)
        ? {
            minAud: Number.isFinite(pricingMin) ? Number(pricingMin.toFixed(2)) : null,
            maxAud: Number.isFinite(pricingMax) ? Number(pricingMax.toFixed(2)) : null,
            medicareRebate: s.pricingMedicare === 'yes',
            notes: (s.pricingNotes || '').trim() || null,
          }
        : null;
    return {
      profileType: "SOLO",
      firstName: (s.firstName || '').trim() || null,
      lastName: (s.lastName || '').trim() || null,
      name: `${s.firstName || ""} ${s.lastName || ""}`.replace(/\s+/g, " ").trim() || null,
      email: contactEmail,
      phone: s.phone || null,
      abn: s.abn ? normalizeAbn(s.abn, countryCode) : null,
      address: {
        street: s.street || '',
        city: s.city || '',
        state: s.state || getDefaultRegionForCountry(countryCode),
        postcode: s.postcode || '',
        country: countryRule.label,
        countryCode,
      },
      ahpraNumber: s.ahpraNumber || null,
      languages: ["English", ...s.languagesOther.split(",").map((x: string) => x.trim()).filter(Boolean)],
      specialties: s.specialties || [],
      customSpecialties: (s.customSpecialties || []).map((v: string) => `Other: ${v}`),
      practiceName: s.practiceName || null,
      publicPractice: s.publicPractice || s.practiceName || null,
      adminContact: {
        email: publicEmail,
        phone: publicPhone ? toE164Phone(publicPhone, countryCode) : null,
        address: {
          street: publicStreet || '',
          city: publicCity || '',
          state: publicState || '',
          postcode: publicPostcode || '',
          country: countryRule.label,
          countryCode,
        },
        practiceName: s.practiceName || null,
      },
      publicEmail,
      publicPhone,
      publicStreet,
      publicState,
      publicPostcode: publicPostcode || '',
      pricing,
      currency: feeCurrency,
      documents: docKeys,
      modes: ["TELEHEALTH"],
      availability: [],
      feeRange: null,
      subspecialties: [],
      profile: s.profile?.trim() || null,
    };
  }, [loginEmail, s, syncPublicContact, docKeys, countryCode, countryRule, feeCurrency]);

  const saveDraft = useCallback(async () => {
    if (isAdmin) return;
    try {
      setSaveVisible(true);
      setSaveStatus("saving");
      const token = await getTokenOrThrow();
      const payload = buildDraftPayload();
      await fetch(DOCTOR_API_BASE + "/work/doctors/draft", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });
      setSaveStatus("saved");
    } catch (e) {
      setSaveStatus("error");
    }
  }, [buildDraftPayload, isAdmin]);
  const syncPublicInitRef = useRef(false);
  const profileVersionRef = useRef<string | null>(null);
  const prefillFromProfile = useCallback((data: any, opts?: { force?: boolean }) => {
    if (!data) return;
    if (!opts?.force && prefillRef.current) return;
    const hasDetails = data.specialties?.length || data.customSpecialties?.length || data.ahpraNumber || data.address || data.practiceName || data.draftProfile;
    if (!hasDetails) return;
    prefillRef.current = true;
    const draft = isAdmin ? null : data.draftProfile || null;
    const source = draft || data;
    const admin = data.adminContact || {};
    const adminAddr = admin.address || {};
    const baseAddr = data.address || {};
    const pricing = data.pricing || {};
    const affType = String(data.practiceAffiliation?.type || data.affiliationRequest || "").toUpperCase();
    const wantsEucaMdAffiliation = affType === "EUCA_MD";
    setS((prev: any) => {
      const compositeName =
        data.firstName || data.lastName
          ? `${data.firstName || ''} ${data.lastName || ''}`
          : data.name || `${prev.firstName || ''} ${prev.lastName || ''}` || '';
      const parsed = splitFullName(compositeName);
      const fallbackEmail = loginEmail || prev.email || data.email || '';
      const normalizedCountryCode = normalizeCountryCode(
        source.countryCode ||
        baseAddr.countryCode ||
        adminAddr.countryCode ||
        source.country ||
        baseAddr.country ||
        adminAddr.country ||
        prev.countryCode,
      );
      return {
        ...prev,
        firstName: data.firstName || parsed.first || prev.firstName,
        lastName: data.lastName || parsed.last || prev.lastName,
      email: fallbackEmail,
      countryCode: normalizedCountryCode,
      phone: data.phone || prev.phone,
      street: baseAddr.street || prev.street,
      city: baseAddr.city || baseAddr.suburb || baseAddr.town || prev.city,
      abn: data.abn || prev.abn,
      state: baseAddr.state || prev.state || getDefaultRegionForCountry(normalizedCountryCode),
      postcode: baseAddr.postcode || baseAddr.postalCode || prev.postcode,
      ahpraNumber: data.ahpraNumber || prev.ahpraNumber,
      languagesOther: Array.isArray(data.languages)
        ? data.languages.filter(l => l && l !== 'English').join(', ')
        : prev.languagesOther,
      specialties: Array.isArray(data.specialties) && data.specialties.length ? data.specialties : prev.specialties,
      customSpecialties: Array.isArray(data.customSpecialties) && data.customSpecialties.length ? data.customSpecialties : prev.customSpecialties,
      profile: data.profile || prev.profile,
      practiceName: data.practiceName || prev.practiceName || '',
      publicPractice: data.practiceName || prev.publicPractice || '',
      publicEmail: admin.email || prev.publicEmail || data.email || '',
      publicPhone: admin.phone || prev.publicPhone || data.phone || '',
      publicStreet: adminAddr.street || prev.publicStreet || '',
      publicCity: adminAddr.city || adminAddr.suburb || adminAddr.town || prev.publicCity || '',
      publicState: baseAddr.state || prev.publicState || getDefaultRegionForCountry(normalizedCountryCode),
      publicPostcode: baseAddr.postcode || baseAddr.postalCode || prev.publicPostcode || '',
        pricingMin: pricing.minAud != null ? String(pricing.minAud) : prev.pricingMin,
        pricingMax: pricing.maxAud != null ? String(pricing.maxAud) : prev.pricingMax,
        pricingMedicare: pricing.medicareRebate === false ? 'no' : 'yes',
        pricingNotes: pricing.notes || prev.pricingNotes || '',
      };
    });
    const docs = source.documents || {};
    setDocKeys((prev) => ({
      photoKey: docs.photoKey || prev.photoKey,
      ahpraKey: docs.ahpraKey || prev.ahpraKey,
      id1Key: docs.id1Key || prev.id1Key,
      cvKey: docs.cvKey || prev.cvKey,
    }));
    setStripeIdentityVerified(
      (prev) =>
        prev ||
        hasStripeIdentityVerified(data) ||
        hasStripeIdentityVerified(source)
    );
  }, [loginEmail, isAdmin]);
  const validatePricing = useCallback(() => {
    const errs: Record<string, string> = {};
    const min = Number(s.pricingMin);
    const max = Number(s.pricingMax);
    if (!Number.isFinite(min)) {
      errs.min = 'Enter a minimum fee';
    } else if (min < 0 || min > 10000) {
      errs.min = 'Must be between $0 and $10,000';
    }
    if (!Number.isFinite(max)) {
      errs.max = 'Enter a maximum fee';
    } else if (max < 0 || max > 10000) {
      errs.max = 'Must be between $0 and $10,000';
    }
    if (Number.isFinite(min) && Number.isFinite(max) && max < min) {
      errs.max = 'Max must be greater than or equal to min';
    }
    const medicare = s.pricingMedicare || 'yes';
    const notes = (s.pricingNotes || '').trim();
    if (notes.length > 200) {
      errs.notes = 'Notes must be 200 characters or less';
    }
    setPricingErrors(errs);
    if (Object.keys(errs).length) return null;
    return {
      minAud: Number(min.toFixed(2)),
      maxAud: Number(max.toFixed(2)),
      medicareRebate: medicare === 'yes',
      notes,
    };
  }, [s.pricingMin, s.pricingMax, s.pricingMedicare, s.pricingNotes]);
  const hasIdentityProof = !!docKeys.id1Key || stripeIdentityVerified;
  // base validation — now: telehealth-only, so we force: address, AHPRA, identity proof, photo
  const canSubmitSpecBase = useMemo(() => {
    const contactEmail = (loginEmail || s.email || '').trim();
    const abnOk = !s.abn || isValidAbn(s.abn, countryCode);
    return !!(
      s.firstName &&
      s.lastName &&
      contactEmail &&
      s.phone &&
      isValidPhoneForCountry(s.phone, countryCode) &&
      abnOk &&
      (countryRule.requireRegion ? s.state : true) &&
      (countryRule.requirePostal ? (s.postcode && isValidPostalCode(countryCode, s.postcode)) : true) &&
      s.ahpraNumber &&
      s.specialties.length > 0 &&
      docKeys.photoKey &&
      docKeys.ahpraKey &&
      hasIdentityProof &&
      docKeys.cvKey &&
      (s.profile || '').trim().length > 0
    );
  }, [s, loginEmail, docKeys, countryCode, countryRule, hasIdentityProof]);
  const validateStep = useCallback((idx: number): string[] => {
    const issues: string[] = [];
    if (idx === 0) {
      if (!s.firstName || !s.lastName) issues.push("Add your first and last name.");
      const contactEmail = (loginEmail || s.email || "").trim();
      if (!contactEmail) issues.push("Email is required.");
      if (!s.phone || !isValidPhoneForCountry(s.phone, countryCode)) {
        issues.push("Add a valid phone number for the selected country.");
      }
      if (s.abn && !isValidAbn(s.abn, countryCode)) {
        issues.push(taxIdError);
      }
      if (!s.ahpraNumber) issues.push(doctorRegistrationRule.requiredError);
      if (!s.street.trim()) issues.push("Add your street address.");
      if (!s.city.trim()) issues.push("Add your suburb/city.");
      if (countryRule.requireRegion && !s.state) issues.push(`Select your ${countryRule.regionLabel.toLowerCase()}.`);
      if (countryRule.requirePostal && !s.postcode) issues.push(`Add your ${countryRule.postalLabel.toLowerCase()}.`);
      if (s.postcode && !isValidPostalCode(countryCode, s.postcode)) {
        issues.push(`Enter a valid ${countryRule.postalLabel.toLowerCase()}.`);
      }
    } else if (idx === 1) {
      if (!s.specialties.length) issues.push("Select at least one specialty.");
    } else if (idx === 2) {
      if (!(s.profile || "").trim()) issues.push("Add your public profile.");
      const pricingOk = validatePricing();
      if (!pricingOk) issues.push("Fix pricing errors.");
    } else if (idx === 3) {
      if (!docKeys.cvKey) issues.push("Upload your CV / Resume, Portfolio, or Supporting Documents.");
      if (!docKeys.ahpraKey) {
        issues.push(
          doctorRegistrationRule.documentMissingError || 'Upload your registration evidence.'
        )
      }
      if (!hasIdentityProof) issues.push("Upload a government-issued photo ID or complete Stripe ID verification.");
      if (!docKeys.photoKey) issues.push("Upload a profile photo.");
      if (!docKeys.cvKey || !docKeys.ahpraKey || !hasIdentityProof || !docKeys.photoKey) {
        // show only once instead of stacking messages
        return issues.length ? [issues[0]] : [];
      }
    }
    return issues;
  }, [s, loginEmail, docKeys, validatePricing, countryCode, countryRule, hasIdentityProof, doctorRegistrationRule]);
  const validateAllSteps = useCallback(() => {
    for (let i = 0; i < steps.length; i++) {
      const issues = validateStep(i);
      if (issues.length) return { ok: false, index: i, message: issues[0] };
    }
    return { ok: true, index: steps.length - 1, message: "" };
  }, [steps.length, validateStep]);
  const docsChecklist = [
    { label: "CV / Resume, Portfolio, or Supporting Documents", ok: !!docKeys.cvKey, name: fileNameFromKey(docKeys.cvKey) },
    {
      label: doctorRegistrationRule.documentLabel || 'Registration evidence',
      ok: !!docKeys.ahpraKey,
      name: fileNameFromKey(docKeys.ahpraKey),
    },
    {
      label: "Government-issued photo ID upload OR Stripe ID verification",
      ok: hasIdentityProof,
      name: docKeys.id1Key ? fileNameFromKey(docKeys.id1Key) : (stripeIdentityVerified ? "Stripe verified" : ""),
    },
    { label: "Profile photo", ok: !!docKeys.photoKey, name: fileNameFromKey(docKeys.photoKey) },
  ];
  const docErrorResetDeps = [docKeys.cvKey, docKeys.ahpraKey, docKeys.id1Key, docKeys.photoKey, stripeIdentityVerified];
  useEffect(() => {
    if (step === 3 && showErrors && validatedStep === 3) {
      // User is on docs step and has interacted with uploads; clear banner until next validation
      setErr("");
      setShowErrors(false);
      setValidatedStep(null);
      setDocsAttempted(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, docErrorResetDeps);
  const docErrorsVisible = showErrors && docsAttempted && validatedStep === 3 && step === 3;
  // after Turnstile token → submit
  useEffect(() => {
    if (waitingSpecTS && tSpec) {
      setWaitingSpecTS(false);
      submitSpecialist();
    }
  }, [waitingSpecTS, tSpec]);
  useEffect(() => {
    if (isAdmin || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await stripeIdentityStatus();
        if (!cancelled && Boolean(res?.verified)) {
          setStripeIdentityVerified(true);
        }
      } catch {
        // best effort only
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user]);
  useEffect(() => {
    if (!initialProfile) return;
    const version =
      initialProfile.updatedAt ||
      initialProfile.status ||
      JSON.stringify({
        name: initialProfile.name,
        profile: initialProfile.profile,
        specialties: initialProfile.specialties,
      });
    if (profileVersionRef.current === version) return;
    profileVersionRef.current = version;
    prefillRef.current = false;
    prefillFromProfile(initialProfile, { force: true });
  }, [initialProfile, prefillFromProfile]);
  // autosave draft
  useEffect(() => {
    if (!isAuthed || loadingStatus) return;
    if (isAdmin) return;
    if (regStatus === "APPROVED") return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    setSaveVisible(true);
    setSaveStatus("saving");
    saveTimerRef.current = window.setTimeout(() => {
      saveDraft();
      saveTimerRef.current = null;
    }, 800);
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [s, docKeys, syncPublicContact, isAuthed, loadingStatus, regStatus, saveDraft, isAdmin]);
  useEffect(() => {
    if (syncPublicInitRef.current) return;
    if (!initialProfile) return;
    const admin = initialProfile.adminContact || {};
    const adminAddr = admin.address || {};
    const hasCustomPublic = Boolean(
      (initialProfile.practiceName && initialProfile.practiceName.trim()) ||
        (admin.email && admin.email.trim()) ||
        (admin.phone && admin.phone.trim()) ||
        (adminAddr.street && adminAddr.street.trim()) ||
        (adminAddr.state && adminAddr.state.trim()) ||
        (adminAddr.postcode && adminAddr.postcode.trim()) ||
        (adminAddr.postalCode && String(adminAddr.postalCode).trim())
    );
    setSyncPublicContact(!hasCustomPublic);
    syncPublicInitRef.current = true;
  }, [initialProfile]);
useEffect(() => {
  if (!syncPublicContact) return;
  setS((prev: any) => {
      const contactEmail = (loginEmail || prev.email || '').trim();
      const desired = {
          publicEmail: contactEmail || '',
          publicPhone: prev.phone || '',
          publicStreet: prev.street || '',
          publicCity: prev.city || '',
          publicState: prev.state || '',
          publicPostcode: prev.postcode || '',
      };
      let changed = false;
      const next: any = { ...prev };
      (Object.keys(desired) as Array<keyof typeof desired>).forEach((key) => {
        if (prev[key] !== desired[key]) {
          next[key] = desired[key];
          changed = true;
        }
      });
      return changed ? next : prev;
  });
}, [syncPublicContact, s.email, s.phone, s.state, s.postcode, s.practiceName, loginEmail]);
  const pricingPreview = useMemo(() => {
    const parts: string[] = [];
    const min = Number(s.pricingMin);
    const max = Number(s.pricingMax);
    if (Number.isFinite(min) && Number.isFinite(max)) {
      parts.push(`$${min.toFixed(2)} – $${max.toFixed(2)} ${feeCurrency}`);
    } else if (Number.isFinite(min)) {
      parts.push(`From $${min.toFixed(2)} ${feeCurrency}`);
    }
    parts.push(`Medicare rebate: ${s.pricingMedicare === 'yes' ? 'Yes' : 'No'}`);
    if (s.pricingNotes.trim()) parts.push(s.pricingNotes.trim());
    return parts.filter(Boolean).join(' · ') || '—';
  }, [s.pricingMin, s.pricingMax, s.pricingMedicare, s.pricingNotes, feeCurrency]);
  const handlePublicContactToggle = useCallback((checked: boolean) => {
    setSyncPublicContact(checked);
    setS((prev: any) => {
      const contactDefaults = {
        publicPractice: prev.practiceName || prev.publicPractice || '',
        publicEmail: (loginEmail || prev.email || prev.publicEmail || '').trim(),
        publicPhone: prev.phone || prev.publicPhone || '',
        publicStreet: '',
        publicState: prev.state || prev.publicState || '',
        publicPostcode: prev.postcode || prev.publicPostcode || '',
      };
      if (checked) {
        return {
          ...prev,
          ...contactDefaults,
        };
      }
      return {
        ...prev,
        ...contactDefaults,
      };
    });
  }, [loginEmail]);
  const disablePublicFields = syncPublicContact;
  useEffect(() => {
    if (saveStatus === "saved" || saveStatus === "error") {
      const t = window.setTimeout(() => {
        setSaveVisible(false);
        setSaveStatus("idle");
      }, 2000);
      return () => window.clearTimeout(t);
    }
  }, [saveStatus]);

  const customSuggestions = useMemo(() => {
    const q = customSpec.toLowerCase().trim();
    if (q.length < 2) return [];
    const source = [...ALL_SPECIALTIES, ...(s.customSpecialties || []), ...(s.specialties || [])];
    return source
      .filter((sp) => {
        const n = normalizeSpec(sp);
        const nq = normalizeSpec(q);
        return sp.toLowerCase().includes(q) || n.includes(nq) || nq.includes(n);
      })
      .slice(0, 4);
  }, [customSpec, s.customSpecialties, s.specialties]);

  const addCustomSpecialty = () => {
    const val = customSpec.trim();
    if (!val) return;
    const norm = normalizeSpec(val);
    const dupCore = s.specialties.some((sp: string) => normalizeSpec(sp) === norm);
    const dupCustom = s.customSpecialties.some((sp: string) => normalizeSpec(sp) === norm);
    if (dupCore || dupCustom) {
      setCustomSpecErr("Already added (or very similar)");
      return;
    }
    const similar = customSuggestions.some((sp) => {
      const n = normalizeSpec(sp);
      return n === norm || n.includes(norm) || norm.includes(n);
    });
    if (similar) {
      setCustomSpecErr("Very similar exists—please pick the existing one");
      return;
    }
    setS((prev: any) => ({
      ...prev,
      customSpecialties: [...(prev.customSpecialties || []), val],
    }));
    setCustomSpec("");
    setCustomSpecErr("");
  };
  async function submitSpecialist() {
    try {
      setSubmitting(true);
      setErr("");
      if (!isAdmin) {
        await auth.currentUser?.reload();
        await auth.currentUser?.getIdToken(true);
      }
      const validatedPricing = validatePricing();
      if (!validatedPricing) {
        setSubmitting(false);
        return;
      }
      if (!isValidPhoneForCountry(s.phone, countryCode)) {
        setSubmitting(false);
        return setErr("Enter a valid phone number for the selected country");
      }
      if (!isAdmin && !tSpec) {
        setSubmitting(false);
        return setErr("Please complete the Turnstile check.");
      }
      const presignFn = isAdmin && adminPresign ? adminPresign : presign;
      // ---- uploads ----
      let { photoKey, ahpraKey, id1Key, cvKey } = docKeys;
      if (!photoKey && s.photoFile) {
        const psg = await presignFn("photo", s.photoFile.type || "application/octet-stream");
        await uploadToS3(psg, s.photoFile);
        photoKey = fileKeyFromPresign(psg);
      }
      if (!ahpraKey && s.ahpraFile) {
        const psg = await presignFn("ahpra", s.ahpraFile.type || "application/octet-stream");
        await uploadToS3(psg, s.ahpraFile);
        ahpraKey = fileKeyFromPresign(psg);
      }
      if (!id1Key && s.idFile1) {
        const psg = await presignFn("id1", s.idFile1.type || "application/octet-stream");
        await uploadToS3(psg, s.idFile1);
        id1Key = fileKeyFromPresign(psg);
      }
      if (!photoKey || !ahpraKey || (!id1Key && !stripeIdentityVerified)) {
        setSubmitting(false);
        const credentialLabel = String(
          doctorRegistrationRule.documentLabel || 'registration evidence'
        ).toLowerCase()
        return setErr(
          `Please upload a profile photo and ${credentialLabel}, and either upload one ID document or complete Stripe ID verification.`
        );
      }
      const contactEmail = (isAdmin ? loginEmail : (auth.currentUser?.email || user?.email || s.email))?.trim() || '';
      if (!contactEmail) {
        setSubmitting(false);
        return setErr('Email is required');
      }
      const publicPhoneE164 =
        s.publicPhone && isValidPhoneForCountry(s.publicPhone, countryCode)
          ? toE164Phone(s.publicPhone, countryCode)
          : null;
      const publicAddress = {
        street: s.street || s.publicStreet || '',
        city: s.city || s.publicCity || '',
        state: s.state || s.publicState || '',
        postcode: s.postcode || s.publicPostcode || '',
        country: countryRule.label,
        countryCode,
      };
      const payload = {
        profileType: "SOLO",
        firstName: (s.firstName || '').trim() || null,
        lastName: (s.lastName || '').trim() || null,
        name: `${s.firstName || ""} ${s.lastName || ""}`.replace(/\s+/g, " ").trim(),
        email: contactEmail,
        phone: toE164Phone(s.phone, countryCode),
        abn: s.abn ? normalizeAbn(s.abn, countryCode) : null,
        address: {
          street: s.street || '',
          city: s.city || '',
          state: s.state || getDefaultRegionForCountry(countryCode),
          postcode: s.postcode,
          country: countryRule.label,
          countryCode,
        },
        ahpraNumber: s.ahpraNumber,
        languages: ["English", ...s.languagesOther.split(",").map((x: string) => x.trim()).filter(Boolean)],
        specialties: s.specialties,
        customSpecialties: (s.customSpecialties || []).map((v: string) => `Other: ${v}`),
        practiceName: (s.publicPractice || s.practiceName || "").trim() || null,
        adminContact: {
          email: s.publicEmail || contactEmail,
          phone: publicPhoneE164 || null,
          address: publicAddress,
        },
        pricing: validatedPricing,
        currency: feeCurrency,
        documents: {
          cvKey: cvKey || null,
          ahpraKey,
          photoKey,
          id1Key,
        },
        modes: ["TELEHEALTH"],
        availability: [],
        feeRange: null,
        subspecialties: [],
        profile: s.profile?.trim() || null,
        ...(isAdmin ? {} : { turnstileToken: tSpec }),
      };
      setDocKeys((prev) => ({
        ...prev,
        photoKey,
        ahpraKey,
        id1Key,
        cvKey: cvKey || prev.cvKey,
      }));
      if (isAdmin) {
        if (!onAdminSubmit) {
          setSubmitting(false);
          return setErr('Admin submit handler not configured');
        }
        await onAdminSubmit(payload);
        setOk(true);
        setRegStatus("APPROVED");
        setInfo("Profile saved.");
        setSubmitting(false);
        onSubmitSuccess?.();
        return;
      }
      const token = await getTokenOrThrow();
      const r = await fetch(DOCTOR_API_BASE + "/register/specialist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) {
        setTSpec("");
        setSubmitting(false);
        return setErr(j.error || "Failed");
      }
      // after submit → immediately show pending page
      setOk(true);
      setRegStatus("PENDING");
      setRegProfileType("SOLO");
      setSubmitting(false);
      onSubmitSuccess?.();
    } catch (e: any) {
      setSubmitting(false);
      setErr(e?.message || String(e));
    }
  }

  const handleNext = () => {
    if (submitting) return;
    const issues = validateStep(step);
    if (issues.length) {
      setShowErrors(true);
      setValidatedStep(step);
      setErr(issues[0]);
      setInfo("");
      return;
    }
    setShowErrors(false);
    setValidatedStep(null);
    setErr("");
    setInfo("");
    setStep((s) => Math.min(steps.length - 1, s + 1));
  };

  const handleSubmit = () => {
    if (submitting) return;
    setDocsAttempted(true);
    setShowErrors(true);
    const all = validateAllSteps();
    if (!all.ok) {
      setErr(all.message);
      setInfo("");
      setValidatedStep(all.index);
      setStep(all.index);
      return;
    }
    setErr("");
    setInfo("");
    setValidatedStep(step);
    if (isAdmin) {
      submitSpecialist();
      return;
    }
    if (!SITE_KEY) {
      submitSpecialist();
      return;
    }
    if (tSpec) {
      submitSpecialist();
      return;
    }
    setErr("");
    setShowErrors(false);
    setValidatedStep(null);
    setInfo("Complete the quick verification below to submit.");
    setShowSpecTS(true);
    setWaitingSpecTS(true);
    setSubmitting(true);
  };
  const AuthGate = !isAuthed && !isAdmin ? (
    <div className="banner warn" role="note" style={{ marginBottom: 12 }}>
      <b>First, sign in:</b> Use <a href="/login">Google</a> or request an <a href="/login">email magic link</a>. Then
      come back to finish your registration.
    </div>
  ) : null;
if (isAuthed && loadingStatus) {
  return (
    <div className="page doc-form-shell flex items-center justify-center" style={{ minHeight: "60vh" }}>
      <Spinner label="Checking your provider profile…" size={36} />
    </div>
  );
}
// ✅ approved → show a thin success screen, rest of navigation via bottom dock
if (!isAdmin && isAuthed && !loadingStatus && regStatus === "APPROVED" && !isInline) {
  return (
    <div className="page doc-form-shell" style={{ maxWidth: 720, margin: "0 auto" }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid rgba(0,0,0,0.04)",
          boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
          borderRadius: 18,
          padding: "20px 20px 18px",
          marginTop: 10,
        }}
      >
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 4 }}>You’re approved 🎉</h1>
        <p style={{ marginBottom: 18, lineHeight: 1.6 }}>
          Your doctor profile is live. Keep your availability fresh and reply quickly to stay at the top of recommendations.
        </p>
        <a className="btn-primary" href="/professionals">
          Open Work hub
        </a>
      </div>
    </div>
  );
}
  // 🔒 if pending → show ONLY the pending layout, nothing else
  if (!isAdmin && isAuthed && !loadingStatus && regStatus === "PENDING" && !isInline) {
    return (
      <div className="container page-wide doc-form-shell" style={{ maxWidth: 760 }}>
        <PendingApprovalCard />
      </div>
    );
  }
  const sectionTitle = isInline ? "Update doctor profile" : undefined;
  const sectionDescription = isInline
    ? "Update your profile. Submitting changes will pause your listing until we re-approve it."
    : undefined;
  return (
    <div className="container page-wide doc-form-shell">
      {!isInline && (
      <div className="page-head">
        <div>
          <p className="hero-kicker">Professionals</p>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Stethoscope size={28} />
            Join as a Doctor
          </h1>
          <p className="hint">List your medical expertise, verify your credentials, and start accepting paid match requests nationwide.</p>
        </div>
      </div>
      )}
      <div className="stepper" style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {steps.map((label, idx) => {
          const active = idx === step;
          const done = idx < step;
          const isFuture = idx > step;
          return (
            <button
              key={label}
              type="button"
              className="chip"
              style={{
                background: active ? '#0f766e' : done ? '#e0f2fe' : '#f8fafc',
                color: active ? '#fff' : '#0f172a',
                borderColor: active ? '#0f766e' : '#cbd5e1',
                cursor: active ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (idx === step) return;
                // Only validate the current step when moving forward; don’t block future steps on their own requirements.
                if (isFuture) {
                  const issues = validateStep(step);
                  if (issues.length) {
                    setShowErrors(true);
                    setValidatedStep(step);
                    setErr(issues[0]);
                    return;
                  }
                }
                setShowErrors(false);
                setValidatedStep(null);
                setErr("");
                setStep(idx);
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      <Section title={sectionTitle}>
        {sectionDescription && (
          <p className="sub" style={{ marginBottom: 16 }}>
            {sectionDescription}
          </p>
        )}
        {!isInline && AuthGate}
        {isInline && inlineNotice && (
          <div className="banner warn" style={{ marginBottom: 16 }}>
            {inlineNotice}
          </div>
        )}
        <form
          className="card doc-form-card"
          style={{ padding: isInline ? '0 0 1.5rem' : '1.5rem', paddingBottom: '6rem' }}
          onSubmit={(e) => e.preventDefault()}
        >
          <section className="section" style={{ display: step === 0 ? 'block' : 'none' }}>
            <h2 className="section-title">Identity & Compliance</h2>
            <div className="two-up">
              <label className="field">
                <div className="field-label">First name *</div>
                <input className="input" placeholder="First name" value={s.firstName} onChange={(e) => setS({ ...s, firstName: e.target.value })} />
              </label>
              <label className="field">
                <div className="field-label">Last name *</div>
                <input className="input" placeholder="Last name" value={s.lastName} onChange={(e) => setS({ ...s, lastName: e.target.value })} />
              </label>
            </div>
            <label className="field">
              <div className="field-label">Email *</div>
              <input className="input" value={s.email} readOnly />
            </label>
            <label className="field">
              <div className="field-label">Country / Territory *</div>
              <select
                className="input"
                value={countryCode}
                onChange={(e) => {
                  const nextCode = normalizeCountryCode(e.target.value);
                  const nextRule = getCountryRule(nextCode);
                  const nextRegions = nextRule.regionOptions || [];
                  setS((prev: any) => {
                    const nextState = nextRegions.length
                      ? (nextRegions.includes(prev.state) ? prev.state : (nextRule.defaultRegion || nextRegions[0] || ""))
                      : '';
                    return {
                      ...prev,
                      countryCode: nextCode,
                      state: nextState,
                      postcode: nextRule.requirePostal ? prev.postcode : "",
                    };
                  });
                }}
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </label>
            <div className="two-up">
              <label className="field">
                <div className="field-label">Mobile *</div>
                <input className="input" placeholder={phonePlaceholder} value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} />
              </label>
              <label className="field">
                <div className="field-label">{doctorRegistrationRule.fieldLabelRequired}</div>
                <input
                  className="input"
                  value={s.ahpraNumber}
                  onChange={(e) => setS({ ...s, ahpraNumber: e.target.value })}
                  placeholder={doctorRegistrationRule.placeholder}
                />
              </label>
            </div>
            <label className="field">
              <div className="field-label">Street address *</div>
              <input className="input" placeholder="Unit / street" value={s.street} onChange={(e) => setS({ ...s, street: e.target.value })} />
            </label>
            <div className="two-up">
              <label className="field">
                <div className="field-label">Suburb / City *</div>
                <input className="input" placeholder="Suburb or city" value={s.city} onChange={(e) => setS({ ...s, city: e.target.value })} />
              </label>
              <label className="field">
                <div className="field-label">
                  {countryRule.regionLabel}
                  {countryRule.requireRegion ? " *" : " (optional)"}
                </div>
                {regionOptions.length > 0 ? (
                  <select className="input" value={s.state} onChange={(e) => setS({ ...s, state: e.target.value as any })}>
                    {!countryRule.requireRegion && <option value="">Select {countryRule.regionLabel}</option>}
                    {regionOptions.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input"
                    placeholder={countryRule.regionLabel}
                    value={s.state}
                    onChange={(e) => setS({ ...s, state: e.target.value })}
                  />
                )}
              </label>
            </div>
            {countryRule.requirePostal && (
              <label className="field">
                <div className="field-label">{countryRule.postalLabel} *</div>
                <input
                  className="input"
                  placeholder={countryRule.postalPlaceholder}
                  value={s.postcode}
                  onChange={(e) => setS({ ...s, postcode: e.target.value })}
                />
              </label>
            )}
          </section>

        <div className="section" style={{ marginTop: -8, display: step === 0 ? 'block' : 'none' }}>
          <button type="button" className="btn-outline" onClick={() => setShowOptional((v) => !v)}>
            {showOptional ? 'Hide optional details' : `Show optional details (${taxIdShortLabel}, public contact)`}
          </button>
          {showOptional && (
            <div className="card" style={{ marginTop: 12, padding: 12 }}>
              <label className="field">
                <div className="field-label">{taxIdLabel}</div>
                <input className="input" value={s.abn} onChange={(e) => setS({ ...s, abn: e.target.value })} placeholder={taxIdPlaceholder} />
                {s.abn && !isValidAbn(s.abn, countryCode) && <div className="field-help" style={{ color: 'var(--error)' }}>{taxIdError}</div>}
              </label>
              <label className="field">
                <div className="field-label">Business / trading name (optional)</div>
                <input className="input" value={s.practiceName} onChange={(e) => setS({ ...s, practiceName: e.target.value })} placeholder="e.g. Dr Smith Clinic" />
              </label>

              <div style={{ marginTop: 12 }}>
                <h3 className="field-label">Public contact (optional)</h3>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={syncPublicContact}
                    onChange={(e) => handlePublicContactToggle(e.target.checked)}
                  />
                  <span>Use the same email and phone for clients. (Region/postal details are shared.)</span>
                </label>
                {!syncPublicContact && (
                  <>
                    <label className="field">
                      <div className="field-label">Public email</div>
                      <input
                        className="input"
                        type="email"
                        value={s.publicEmail}
                        onChange={(e) => setS({ ...s, publicEmail: e.target.value })}
                        disabled={disablePublicFields}
                      />
                    </label>
                    <label className="field">
                      <div className="field-label">Public phone</div>
                      <input
                        className="input"
                        value={s.publicPhone}
                        onChange={(e) => setS({ ...s, publicPhone: e.target.value })}
                        placeholder={phonePlaceholder}
                        disabled={disablePublicFields}
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

          <section className="section" style={{ display: step === 1 ? 'block' : 'none' }}>
            <h2 className="section-title">Area of Expertise</h2>
            <div className="label-row">
              <div className="field-label">Specialties <span className="required">*</span></div>
              <div className="hint">Select all that apply. We won’t limit you.</div>
            </div>
            <div className="chips">
              {s.specialties.map((sp: string) => (
                <span key={sp} className="chip">
                  {sp}
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={`Remove ${sp}`}
                    onClick={() => setS({ ...s, specialties: s.specialties.filter((x: string) => x !== sp) })}
                  >
                    ×
                  </button>
                </span>
              ))}
              {(s.customSpecialties || []).map((sp: string) => (
                <span key={`custom-${sp}`} className="chip">
                  {`Other: ${sp}`}
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={`Remove ${sp}`}
                    onClick={() =>
                      setS((prev: any) => ({
                        ...prev,
                        customSpecialties: prev.customSpecialties.filter((x: string) => x !== sp),
                      }))
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
              {!s.specialties.length && !(s.customSpecialties?.length) && <span className="hint">No specialties selected</span>}
            </div>
            <button type="button" className="btn-outline" onClick={() => setSpecOpen(true)}>
              {(s.specialties.length || (s.customSpecialties?.length || 0) > 0) ? "Edit specialties" : "Add specialties"}
            </button>
            <label className="field" style={{ marginTop: 20 }}>
              <div className="field-label">Public profile (shown to clients after you accept) <span className="required">*</span></div>
              <textarea
                className="input"
                rows={5}
                maxLength={PROFILE_CHAR_LIMIT}
                placeholder="Short bio, scope of work, languages, client groups you like to see…"
                value={s.profile}
                onChange={(e) => setS({ ...s, profile: e.target.value })}
              />
              <div className="hint" style={{ textAlign: 'right' }}>{`${s.profile.length}/${PROFILE_CHAR_LIMIT} characters`}</div>
            </label>
          </section>

          <section className="section" style={{ display: step === 2 ? 'block' : 'none' }}>
            <h2 className="section-title">Profile & Pricing</h2>
            <p className="hint" style={{ marginBottom: 12 }}>
              These fees appear once you accept a match. Use the notes for concessions or other context clients need.
            </p>
            <div className="two-up">
              <label className="field">
                <div className="field-label">{`Minimum fee (${feeCurrency}) per 30 min session *`}</div>
                <input className="input" type="number" min={0} max={10000} step="1" value={s.pricingMin} onChange={(e) => setS({ ...s, pricingMin: e.target.value })} placeholder="e.g. 150" />
                {pricingErrors.min && <div className="field-help" style={{ color: 'var(--error)' }}>{pricingErrors.min}</div>}
              </label>
              <label className="field">
                <div className="field-label">{`Maximum fee (${feeCurrency}) per 30 min session *`}</div>
                <input className="input" type="number" min={0} max={10000} step="1" value={s.pricingMax} onChange={(e) => setS({ ...s, pricingMax: e.target.value })} placeholder="e.g. 250" />
                {pricingErrors.max && <div className="field-help" style={{ color: 'var(--error)' }}>{pricingErrors.max}</div>}
              </label>
            </div>
            <label className="field">
              <div className="field-label">Medicare rebate available?</div>
              <select className="input" value={s.pricingMedicare} onChange={(e) => setS({ ...s, pricingMedicare: e.target.value })}>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="field">
              <div className="field-label">Notes (optional, 200 chars max)</div>
              <textarea className="input" rows={2} maxLength={200} value={s.pricingNotes} onChange={(e) => setS({ ...s, pricingNotes: e.target.value })} placeholder="e.g. Concessions available for pensioners." />
              {pricingErrors.notes && <div className="field-help" style={{ color: 'var(--error)' }}>{pricingErrors.notes}</div>}
            </label>
          </section>

          <section className="section" style={{ display: step === 3 ? 'block' : 'none' }}>
            <h2 className="section-title">Documents & Verification</h2>
            {!isInline ? (
              <>
                <div className="card" style={{ marginBottom: 12, padding: 12 }}>
                  <div className="field-label" style={{ marginBottom: 4 }}>Identity verification (choose ONE option)</div>
                  <p className="identity-choice-help">Choose ONE option only for ID verification: Option A complete Stripe verification OR Option B upload your photo ID. Do not complete both.</p>
                  <div className="inline-gap-2" style={{ flexWrap: 'wrap' }}>
                    {docsChecklist.map((row) => (
                      <div
                        key={row.label}
                        className="chip"
                        style={{
                          background: row.ok ? '#ecfdf3' : '#f8fafc',
                          borderColor: row.ok ? '#16a34a' : '#cbd5e1',
                          color: row.ok ? '#166534' : '#0f172a',
                        }}
                      >
                        {row.ok ? <CheckCircle2 size={16} style={{ marginRight: 6 }} /> : <span style={{ marginRight: 6 }}>•</span>}
                        <span>
                          {row.label}
                          {row.ok && row.name ? ` — ${row.name}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="two-up">
                  <FilePickerField
                    label="Upload CV / Resume, Portfolio, or Supporting Documents"
                    required
                    requiredColor="#6b7280"
                    uploadedLabel={fileNameFromKey(docKeys.cvKey)}
                    value={s.cvFile}
                    onChange={(file) => handleDocSelect("cvKey", file, "cv")}
                  />
                  <FilePickerField
                    label={
                      doctorRegistrationRule.documentUploadLabel ||
                      "Upload registration evidence"
                    }
                    required
                    requiredColor="#6b7280"
                    uploadedLabel={fileNameFromKey(docKeys.ahpraKey)}
                    value={s.ahpraFile}
                    onChange={(file) => handleDocSelect("ahpraKey", file, "ahpra")}
                  />
                </div>
                <FilePickerField
                  label="Profile photo"
                  required
                  requiredColor="#6b7280"
                  uploadedLabel={fileNameFromKey(docKeys.photoKey)}
                  value={s.photoFile}
                  onChange={(file) => handleDocSelect("photoKey", file, "photo")}
                  hint="We’ll show your profile photo to clients once you’re verified."
                />
                <div className="two-up">
                  <StripeIdentityVerifyPanel
                    className="identity-choice-card identity-choice-card--stripe"
                    enabled={!isAdmin && !!user}
                    countryCode={countryCode}
                    onChange={(snap) => setStripeIdentityVerified(Boolean(snap.verified))}
                  />
                  <div className="identity-choice-or" aria-hidden="true">OR</div>
                  <FilePickerField
                    className="identity-choice-card identity-choice-card--upload"
                    label="Option B: Upload government-issued photo ID"
                    uploadedLabel={fileNameFromKey(docKeys.id1Key)}
                    value={s.idFile1}
                    onChange={(file) => handleDocSelect("id1Key", file, "id1")}
                    hint="Option B (upload): Passport, driver licence, or national ID card. No Stripe fee."
                  />
                </div>
              </>
            ) : (
              <p className="hint" style={{ marginTop: 12 }}>
                Need to update credentials? Email contact@eucamatch.com with your documents.
              </p>
            )}
          </section>

        {showSpecTS && !tSpec && !isAdmin && (
          <section className="section">
            <label className="field-label">Quick verification</label>
            <Turnstile
              siteKey={SITE_KEY}
                action="register_specialist"
                onToken={(tok) => {
                  setTSpec(tok);
                  setShowSpecTS(false);
                }}
                theme="auto"
                size="flexible"
              />
            </section>
          )}

          <section className="section">
            {err && showErrors && validatedStep === step && (
              <div className="banner error" role="alert" style={{ marginBottom: 10 }}>
                {err}
              </div>
            )}
            <div className="actions" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <button
                className="btn-outline"
                type="button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0 || submitting}
              >
                Back
              </button>
              {step < steps.length - 1 ? (
                <button
                  className="btn-primary"
                  type="button"
                  disabled={submitting}
                  onClick={handleNext}
                >
                  Next
                </button>
              ) : (
                <button className="btn-primary" type="button" onClick={handleSubmit} disabled={!isAuthed || submitting}>
                  {submitting ? "Submitting…" : "Submit registration"}
                </button>
              )}
              {!isAdmin && step < steps.length - 1 && (
                <button
                  className="btn-outline"
                  type="button"
                  disabled={submitting}
                  onClick={async () => {
                    try {
                      setSaveVisible(true);
                      setSaveStatus("saving");
                      await saveDraft();
                      setInfo("Draft saved. You can finish later from Work hub.");
                      setErr("");
                    } catch (e: any) {
                      setErr("Save failed. Try again.");
                      setInfo("");
                    }
                  }}
                  style={{ marginLeft: 'auto' }}
                >
                  Save & finish later
                </button>
              )}
            </div>
            {ok && (
              <div className="banner success" style={{ marginTop: 6 }}>
                <b>Thanks! Your registration was submitted.</b> We’ll verify your documents and enable matching.
              </div>
            )}
            {info && (
              <div className="banner info" style={{ marginTop: 6 }}>
                {info}
              </div>
            )}
          </section>
        </form>
      </Section>
      {/* Specialties overlay */}
      {specOpen && (
        <div className="overlay show" role="dialog" aria-modal="true">
          <div className="sheet" id="spec-sheet">
            <header className="sheet-header">
              <div className="sheet-title">Select specialties</div>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setSpecOpen(false);
                  setSpecQuery("");
                }}
              >
                Close
              </button>
            </header>
            <div className="sheet-body">
              <input
                className="input"
                placeholder="Search specialties…"
                value={specQuery}
                onChange={(e) => setSpecQuery(e.target.value)}
                aria-label="Search specialties"
              />
              <div className="spec-list">
                {Object.entries(SPECIALTY_GROUPS).map(([group, list]) => {
                  const filtered = list.filter((sp) => sp.toLowerCase().includes(specQuery.toLowerCase()));
                  if (!filtered.length) return null;
                  const current = s.specialties;
                  return (
                    <div key={group} className="card" style={{ marginBottom: 12 }}>
                      <div className="field-label" style={{ marginBottom: 8 }}>{group}</div>
                      <div className="chip-row">
                        {filtered.map((sp) => {
                          const checked = current.includes(sp);
                          return (
                            <button
                              type="button"
                              key={sp}
                              className={`chip ${checked ? 'active' : ''}`}
                              onClick={() => {
                                const cur = s.specialties;
                                if (checked) {
                                  setS({
                                    ...s,
                                    specialties: cur.filter((x: string) => x !== sp),
                                  });
                                } else {
                                  setS({
                                    ...s,
                                    specialties: [...cur, sp],
                                  });
                                }
                              }}
                            >
                              {sp}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {ALL_SPECIALTIES.filter((sp) => sp.toLowerCase().includes(specQuery.toLowerCase())).length === 0 && (
                  <div className="hint" style={{ padding: 8 }}>
                    No results. Try a different term.
                  </div>
                )}
              </div>
              <div className="card" style={{ marginTop: 12 }}>
                <div className="field-label" style={{ marginBottom: 6 }}>Other areas (short text)</div>
                <div className="inline-gap-2" style={{ alignItems: 'flex-start' }}>
                  <input
                    className="input"
                    placeholder="Add another area"
                    value={customSpec}
                    onChange={(e) => {
                      setCustomSpec(e.target.value);
                      setCustomSpecErr("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCustomSpecialty();
                      }
                    }}
                  />
                  <button type="button" className="btn-outline" onClick={addCustomSpecialty}>
                    Add
                  </button>
                </div>
                {(s.customSpecialties || []).length > 0 && (
                  <div className="chip-row" style={{ marginTop: 8 }}>
                    {(s.customSpecialties || []).map((sp: string) => (
                      <button
                        type="button"
                        key={`custom-${sp}`}
                        className="chip active"
                        onClick={() =>
                          setS((prev: any) => ({
                            ...prev,
                            customSpecialties: prev.customSpecialties.filter((x: string) => x !== sp),
                          }))
                        }
                        aria-label={`Remove ${sp}`}
                      >
                        {`Other: ${sp}`} ×
                      </button>
                    ))}
                  </div>
                )}
                {customSpecErr ? (
                  <div className="hint" style={{ color: "#dc2626", marginTop: 6 }}>{customSpecErr}</div>
                ) : (
                  <div className="hint" style={{ marginTop: 6 }}>
                    {customSuggestions.length ? (
                      <>Similar existing: {customSuggestions.join(" · ")}</>
                    ) : (
                      "Add any other areas that aren’t listed above."
                    )}
                  </div>
                )}
              </div>
            </div>
            <footer className="sheet-footer">
              <div className="hint">
                {(s.specialties.length + (s.customSpecialties?.length || 0))} selected
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setSpecOpen(false);
                  setSpecQuery("");
                }}
              >
                Done
              </button>
            </footer>
          </div>
        </div>
      )}
      {saveVisible && saveStatus !== "idle" && (
        <div
          style={{
            position: "fixed",
            bottom: 80,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1500,
            background: "#0f766e",
            color: "#fff",
            borderRadius: 999,
            padding: "12px 18px",
            boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 14,
            minWidth: 180,
            justifyContent: "center",
          }}
          aria-live="polite"
        >
          <Stethoscope size={16} />
          <span>
            {saveStatus === "saving" && "Saving draft…"}
            {saveStatus === "saved" && "Saved"}
            {saveStatus === "error" && "Save failed (retrying on next change)"}
          </span>
        </div>
      )}
    </div>
  );
}
const splitFullName = (full: string | null | undefined) => {
  if (!full) return { first: '', last: '' };
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return {
    first: parts.slice(0, -1).join(' '),
    last: parts.slice(-1).join(' '),
  };
};
